import { TokenDTO, TokensRequest, TokensResponse } from '@brain-auth/models/auth-token-dto.model';
import { createAction, props } from '@ngrx/store';

export const initLoginSignIn = createAction('[Login] Init Sign-in');
export const initLoginForgotPassword = createAction('[Login] Init Forgot Password');

export const postLogin = createAction('[Login] Asking for login', props<{ username: string; password: string }>());
export const postLoginSuccess = createAction('[Login] Asking for login success ');
export const postLoginError = createAction('[Login] Asking for login error', props<{ error: Error }>());

// BRN-2208 L5 finding F1 -- the two outcomes `postLoginError` used to swallow. Both are refusals,
// neither is a credential failure, and rendering either as "the user name or password is incorrect"
// is what made the login screen lie:
//
// - ACCOUNT NOT PROVISIONED: Auth0 verified the password; brain-iam then refused because the
//   account has no usable identity link. The only remedy the login card offers -- a password reset
//   -- passes every gate, sends a real Auth0 email, completes, and cannot create the missing row,
//   so the user loops forever on a correct password.
// - UNAVAILABLE: a 5xx or a transport failure. Convergence puts Cloud KMS on the login critical
//   path, so a KMS incident lands here -- and told the entire affected population their passwords
//   were wrong, which also corrupts the operator's own revert signal.
export const postLoginAccountNotProvisioned = createAction('[Login] Login refused -- account not provisioned');
export const postLoginUnavailable = createAction('[Login] Login unavailable -- platform failure', props<{ error: unknown }>());

// BRN-2208 L6 finding L4-R -- the THIRD refusal `postLoginError` swallowed, and the one the committed
// cross-boundary contract named as a live dead end while it was still live. brain-iam returns
// FAILURE_WRONG_USER_STATUS for an ARCHIVED or BLOCKED account: the password was right, the account
// is not admissible, and "the user name or password is incorrect" is a false diagnosis whose only
// offered remedy -- a password reset -- completes successfully and cannot change an account's status.
// Distinct from `postLoginAccountNotProvisioned` on purpose: that user needs enrichment and is sent
// to /session/unenriched; this one needs an administrator and must stay on the login card.
export const postLoginAccountStatusRefused = createAction('[Login] Login refused -- account status');

export const askForChallengeSuccess = createAction('[Login] Asking to get MFA Code success ', props<{ tokenDto: TokenDTO }>());

export const postLoginMfa = createAction('[Login] Login with MFA Code', props<{ tokenDto: TokenDTO }>());
export const postLoginMfaSuccess = createAction('[Login] Login with MFA Code success ', props<{ userAuthenticated: any }>());
export const postLoginMfaError = createAction('[Login] Login with MFA Code error', props<{ error: Error }>());

export const loadUserAuthenticated = createAction('[User] Load User Authenticated');
export const loadUserAuthenticatedSuccess = createAction('[User] Load User Authenticated Success', props<{ userAuthenticated: any }>());
export const loadUserAuthenticatedError = createAction('[User] Load User Authenticated Error', props<{ error: Error }>());

// BRN-2208: somebody IS signed in, but their token carries no usable BRAIN enrichment claims.
// Deliberately distinct from `loadUserAuthenticatedError('no user')`, which said the opposite of
// what had happened -- that conflation is exactly why an unenriched sign-in was indistinguishable
// from a signed-out one, and it made the condition invisible in the store and in devtools.
export const loadUserAuthenticatedUnenriched = createAction('[User] Load User Authenticated Unenriched');

export const postForgotPassword = createAction('[User] Forgot Password', props<{ email: string }>());
export const postForgotPasswordSuccess = createAction('[User] Forgot Password Success');
export const postForgotPasswordError = createAction('[User] Forgot Password Error', props<{ error: Error }>());

export const postTokens = createAction('[Login] Post Tokens', props<{ payload: TokensRequest }>());

export const postTokensSuccess = createAction('[Login] Post Tokens Success', props<{ response: TokensResponse }>());

export const postTokensError = createAction('[Login] Post Tokens Error', props<{ error: any }>());

export const requestOtpChallenge = createAction('[OTP] Request Challenge');
export const requestOtpChallengeSuccess = createAction('[OTP] Request Challenge Success');
export const requestOtpChallengeError = createAction('[OTP] Request Challenge Error', props<{ error: any }>());

export const verifyOtpChallenge = createAction('[OTP] Verify Challenge', props<{ email: string; code: string; facilityGroupId: number }>());
export const verifyOtpChallengeSuccess = createAction('[OTP] Verify Challenge Success');
export const verifyOtpChallengeError = createAction('[OTP] Verify Challenge Error', props<{ error: any }>());

// BRN-2148: SSO callback landing — redeem the one-time handoff code (never a token) for the
// session bundle, then reuse the existing password-flow storage mechanism (saveStorageAndLoadUserProfile).
export const redeemSsoHandoff = createAction('[Login] Redeem SSO Handoff', props<{ code: string }>());
export const redeemSsoHandoffSuccess = createAction('[Login] Redeem SSO Handoff Success');
export const redeemSsoHandoffError = createAction('[Login] Redeem SSO Handoff Error', props<{ error: any }>());
